import { useTheme } from '@mui/material';
import { useMemo } from 'react';
import { isArray, isBoolean, isNumber, isObject, isString, maybeBoolean } from 'validata';
import { useObjectFlag } from '../components';
import { EventDefinition, getEventsDefinition } from '../notable-events';

const notableEventsContract = {
  featureId: isString(),
  eventTypeId: isString(),
  priority: isNumber(),
  enabled: isBoolean(),
  ignoreIds: maybeBoolean(),
  inheritEventColor: maybeBoolean(),
  notableEvent: isBoolean(),
  styling: isObject({
    backgroundColor: isString(),
    textColor: isString(),
  }),
};

const notableEventsValidator = isArray(isObject(notableEventsContract));

export const useNotableEvents = (): EventDefinition[] => {
  const theme = useTheme();
  const notableEvents = useObjectFlag('track-thing-history-notable-events', notableEventsValidator);

  return useMemo(() => {
    const isValid = Array.isArray(notableEvents) && notableEvents.length > 0;

    if (!isValid) return getEventsDefinition(theme);
    return notableEvents.map((event) => ({
      ...event,
      styling: {
        backgroundColor: resolveThemeValue(theme, event.styling.backgroundColor),
        textColor: resolveThemeValue(theme, event.styling.textColor),
      },
    }));
  }, [notableEvents, theme]);
};

const resolveThemeValue = (theme: unknown, value: string): string => {
  if (!value.startsWith('theme.')) return value;

  const path = value.replace('theme.', '').split('.');
  let result: unknown = theme;

  for (const key of path) {
    if (typeof result !== 'object' || result === null) break;

    const arrayMatch = key.match(/(\w+)\[(\d+)\]/);
    const recordResult = result as Record<string, unknown>;

    if (arrayMatch) {
      const arrayName = arrayMatch[1];
      const numericKey = arrayMatch[2];
      const targetObject = recordResult[arrayName];

      if (typeof targetObject === 'object' && targetObject !== null) {
        const objWithNumericKeys = targetObject as Record<string, unknown>;
        if (numericKey in objWithNumericKeys) {
          result = objWithNumericKeys[numericKey];
        } else {
          result = undefined;
        }
      } else {
        result = undefined;
      }
    } else if (key in recordResult) {
      result = recordResult[key];
    } else {
      result = undefined;
    }

    if (result === undefined) break;
  }

  return typeof result === 'string' ? result : value;
};
